import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { getComments, type Api } from "api-client";

export interface Comment {
  id: string;
  content: string;
  createdAt: string;
  isRead: boolean;
  readAt: string | null;
  author: {
    id: string;
    name: string;
    initials: string;
  };
  commentGroupId: string;
  exerciseId: string;
  exerciseName: string;
}

export interface CommentGroupSummary {
  id: string;
  title: string;
  commentCount: number;
  unreadCount: number;
  commentIds: string[];
}

export interface CommentsResponse {
  comments: Comment[];
  commentGroups: CommentGroupSummary[];
  pageInfo: {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    pageIndex: number;
    totalCount: number;
  };
  totals: {
    commentCount: number;
    unreadCount: number;
  };
}

export interface UseCommentsQueryOptions {
  workoutId: string | null;
  participantId: string | null;
  exerciseIds: string[];
  pageIndex?: number;
  pageSize?: number;
}

function getCommentsQueryKey(options: UseCommentsQueryOptions) {
  if (!options.workoutId) {
    return [];
  }

  return [
    "comments",
    options.workoutId,
    options.pageIndex ?? 0,
    options.participantId,
    options.exerciseIds,
  ] as const;
}

export function getCommentsQueryKeyPrefix(workoutId: string) {
  return ["comments", workoutId] as const;
}

export function useCommentsQuery(options: UseCommentsQueryOptions) {
  const query = useQuery({
    queryKey: getCommentsQueryKey(options),
    queryFn: async () => {
      if (!options.workoutId) {
        throw new Error("workoutId is required");
      }

      const response = await getComments({
        query: {
          workoutId: options.workoutId,
          participantId: options.participantId ?? undefined,
          pageIndex: options.pageIndex,
          pageSize: options.pageSize,
        },
      });

      const data = response.data.data;

      const exerciseThreadMap = new Map<
        string,
        Api.GetComments.Output.ExerciseThread
      >();

      for (const thread of data.exerciseThreads) {
        exerciseThreadMap.set(thread.exerciseId, thread);
      }

      return {
        ...data,
        exerciseThreadMap,
      };
    },
    enabled: !!options.workoutId,
    placeholderData: keepPreviousData,
  });

  return query;
}
